import React from 'react';
import {
  Paper,
  Typography,
  Divider,
  Button,
  Grid2 as Grid,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { FieldArray, FieldArrayRenderProps } from 'formik';
import { AddOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { Moment } from 'moment';
import UserEditorBase from '../UserEditorBase';
import FormTextField from '../../Form/FormTextField';
import FileUploadButton from '../../Form/FileUploadButton';

const useStyles = makeStyles((theme: Theme) => ({
  userFormSubContainer: {
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
  },
  userDocumentRow: {
    padding: theme.spacing(2),
  },
  datePicker: {
    width: '100%',
    [`${theme.breakpoints.down('lg')}`]: {
      marginBottom: theme.spacing(1),
    },
  },
}));

type DocumentType = {
  name: string;
  expiryDate: Moment | null;
  url: string;
};

const UserDocumentsEditor = () => {
  const classes = useStyles();

  const handleViewButtonClick = (documentUrl: string) => {
    return window.open(documentUrl);
  };

  return (
    <UserEditorBase>
      <FieldArray name="documents">
        {(fieldArrayProps: FieldArrayRenderProps) => {
          const { push, remove, form } = fieldArrayProps;
          const { values, setFieldValue } = form;
          const { documents }: { documents: DocumentType[] } = values;

          const onDownload = (url: string) => {
            const link = document.createElement('a');
            link.download = url;
            link.href = url;
            link.click();
          };

          return (
            <Paper>
              <div className={classes.userFormSubContainer}>
                <Typography variant="h6">Documents</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddOutlined />}
                  onClick={() =>
                    push({
                      name: '',
                      expiryDate: null,
                      url: '',
                    })
                  }
                >
                  Add File
                </Button>
              </div>
              <Divider orientation="horizontal" />
              {documents.map((document, index) => {
                return (
                  <Grid
                    key={index}
                    className={classes.userDocumentRow}
                    container
                    alignItems="flex-end"
                    spacing={1}
                  >
                    <Grid container size={{ md: 6 }} spacing={1}>
                      <FormTextField
                        label="File Name"
                        size={12}
                        md={6}
                        fullWidth
                        name={`documents[${index}].name`}
                        variant="outlined"
                        value={values.documents[index].name}
                        onChange={e =>
                          setFieldValue(
                            `documents[${index}].name`,
                            e.target.value
                          )
                        }
                      />
                      <Grid size={{ xs: 12, md: 6 }}>
                        <DatePicker
                          label="Expiry date"
                          className={classes.datePicker}
                          value={values.documents[index].expiryDate}
                          onChange={date =>
                            setFieldValue(
                              `documents[${index}].expiryDate`,
                              date
                            )
                          }
                          format="DD/MM/YYYY"
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} size={{ md: 6 }}>
                      <Grid size={{ xs: 4 }}>
                        {documents[index].name && documents[index].url ? (
                          <Button
                            onClick={() => onDownload(documents[index].url)}
                            variant="contained"
                            color="primary"
                          >
                            Download
                          </Button>
                        ) : (
                          <FileUploadButton
                            fullWidth
                            label="upload"
                            variant="contained"
                            id={`user-document-upload-${index}`}
                            handleChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue(
                                `documents[${index}].url`,
                                'https://www.keckmedicine.org/wp-content/uploads/2018/03/Four-friends-toast-before-consuming-alcoholic-drinks..jpg'
                              );
                              if (event.target.files?.[0]) {
                                setFieldValue(
                                  `documents[${index}].name`,
                                  event.target.files[0].name
                                );
                              }
                            }}
                          />
                        )}
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            handleViewButtonClick(values.documents[index].url)
                          }
                          disabled={values.documents[index].url === ''}
                        >
                          View
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Button
                          onClick={() => remove(index)}
                          fullWidth
                          variant="contained"
                          color="primary"
                        >
                          Delete
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </Paper>
          );
        }}
      </FieldArray>
    </UserEditorBase>
  );
};

export default UserDocumentsEditor;
